import { sumMonetaryValues } from "@common/helpers/monetary";
import {
    CheckDetailGroup,
    CheckDetailResponse,
    CheckDetailRow,
} from "@common/schemas/domains/accounting/check-detail-report.schema";
import {
    CheckDetailCheckViewRow,
    CheckDetailGroupBy,
    CheckDetailGroupViewRow,
    CheckDetailSectionKind,
    CheckDetailSectionViewRow,
} from "@common/types/reports/check-detail";

interface RowGroup {
    key: string;
    title: string;
    rank: number;
}

interface BuildSectionParams {
    section: CheckDetailSectionKind;
    title: "Checks" | "Deposits";
    groups: CheckDetailGroup[];
    groupBy: CheckDetailGroupBy;
}

interface BuildGroupRowParams {
    section: CheckDetailSectionKind;
    group: Omit<RowGroup, "rank">;
    rows: CheckDetailRow[];
}

interface GroupRowsParams {
    section: CheckDetailSectionKind;
    rows: CheckDetailRow[];
    getGroup: (row: CheckDetailRow) => RowGroup;
}

function getSectionRowId(
    section: CheckDetailSectionKind,
): `section:${CheckDetailSectionKind}` {
    return `section:${section}`;
}

function toCheckViewRow(check: CheckDetailRow): CheckDetailCheckViewRow {
    return {
        id: `check:${check.transactionId}`,
        rowKind: "check",
        check,
    };
}

function buildGroupRow({
    section,
    group,
    rows,
}: BuildGroupRowParams): CheckDetailGroupViewRow {
    return {
        id: `group:${section}:${group.key}`,
        rowKind: "group",
        title: group.title,
        total: sumMonetaryValues(rows.map((row) => row.amount)),
        children: rows.map(toCheckViewRow),
    };
}

function getReconciliationGroup(row: CheckDetailRow): RowGroup {
    return row.isReconciled
        ? { key: "reconciled", title: "Reconciled", rank: 1 }
        : { key: "unreconciled", title: "Unreconciled", rank: 0 };
}

function getCounterpartyGroup(row: CheckDetailRow): RowGroup {
    return row.counterpartyId
        ? {
              key: `counterparty:${row.counterpartyId}`,
              title: row.payee ?? row.counterpartyId,
              rank: 0,
          }
        : { key: "no-counterparty", title: "No counterparty", rank: 1 };
}

function compareRowsByDate(a: CheckDetailRow, b: CheckDetailRow): number {
    return (
        a.dateOnly.localeCompare(b.dateOnly) ||
        a.transactionId - b.transactionId
    );
}

function groupRows({
    section,
    rows,
    getGroup,
}: GroupRowsParams): CheckDetailGroupViewRow[] {
    const rowsByKey = new Map<
        string,
        { group: RowGroup; rows: CheckDetailRow[] }
    >();
    for (const row of [...rows].sort(compareRowsByDate)) {
        const group = getGroup(row);
        const entry = rowsByKey.get(group.key) ?? { group, rows: [] };
        entry.rows.push(row);
        rowsByKey.set(group.key, entry);
    }
    return [...rowsByKey.values()]
        .sort(
            (a, b) =>
                a.group.rank - b.group.rank ||
                a.group.title.localeCompare(b.group.title),
        )
        .map((entry) => buildGroupRow({ section, ...entry }));
}

function buildSectionChildren({
    section,
    groups,
    groupBy,
}: Omit<BuildSectionParams, "title">): Array<
    CheckDetailGroupViewRow | CheckDetailCheckViewRow
> {
    const rows = groups.flatMap((group) => group.rows);
    switch (groupBy) {
        case "bankAccount":
            return groups.map((group) =>
                buildGroupRow({
                    section,
                    group: {
                        key: String(group.financialAccountId),
                        title: group.financialAccountName,
                    },
                    rows: group.rows,
                }),
            );
        case "reconciliationStatus":
            return groupRows({
                section,
                rows,
                getGroup: getReconciliationGroup,
            });
        case "counterparty":
            return groupRows({ section, rows, getGroup: getCounterpartyGroup });
        case "none":
            return [...rows].sort(compareRowsByDate).map(toCheckViewRow);
        default: {
            const unreachable: never = groupBy;
            return unreachable;
        }
    }
}

function buildSectionRow({
    section,
    title,
    groups,
    groupBy,
}: BuildSectionParams): CheckDetailSectionViewRow {
    return {
        id: getSectionRowId(section),
        rowKind: "section",
        title,
        total: sumMonetaryValues(groups.map((group) => group.total)),
        children: buildSectionChildren({ section, groups, groupBy }),
    };
}

export function buildCheckDetailRows(
    report: CheckDetailResponse,
    groupBy: CheckDetailGroupBy = "bankAccount",
): CheckDetailSectionViewRow[] {
    const sections: CheckDetailSectionViewRow[] = [];
    if (report.groups.length > 0) {
        sections.push(
            buildSectionRow({
                section: "checks",
                title: "Checks",
                groups: report.groups,
                groupBy,
            }),
        );
    }
    if (report.depositGroups.length > 0) {
        sections.push(
            buildSectionRow({
                section: "deposits",
                title: "Deposits",
                groups: report.depositGroups,
                groupBy,
            }),
        );
    }
    return sections;
}

export function getCheckDetailExpandedState(
    report: CheckDetailResponse,
    groupBy: CheckDetailGroupBy = "bankAccount",
): Record<string, boolean> {
    const expanded: Record<string, boolean> = {};
    for (const row of buildCheckDetailRows(report, groupBy)) {
        expanded[row.id] = true;
        for (const child of row.children) {
            if (child.rowKind === "group") {
                expanded[child.id] = true;
            }
        }
    }
    return expanded;
}
